import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Globe, Plus, Search, AlertCircle, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { BACKEND_URL } from '@/lib/backend';
export default function DomainsModule() {
  const [domains, setDomains] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: '', domain_name: '', registrar: '', dns_provider: '',
    expiry_date: '', auto_renew: false, notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [domRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/domains`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (domRes.ok) setDomains(await domRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/domains`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowAddDialog(false);
        loadData();
      }
    } catch (err) {
      console.error('Failed to create domain:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/domains/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete domain:', err);
    }
  };

  const filtered = domains.filter(d =>
    d.domain_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Domains</h1>
          <p className="text-sm text-slate-500 mt-1">Domain registration tracking</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />Add Domain
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add New Domain</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Domain Name *</Label><Input value={formData.domain_name} onChange={(e) => setFormData({...formData, domain_name: e.target.value})} placeholder="example.com" required /></div>
                <div><Label>Registrar</Label><Input value={formData.registrar} onChange={(e) => setFormData({...formData, registrar: e.target.value})} placeholder="GoDaddy" /></div>
                <div><Label>DNS Provider</Label><Input value={formData.dns_provider} onChange={(e) => setFormData({...formData, dns_provider: e.target.value})} placeholder="Cloudflare" /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={formData.expiry_date} onChange={(e) => setFormData({...formData, expiry_date: e.target.value})} /></div>
                <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={formData.auto_renew} onChange={(e) => setFormData({...formData, auto_renew: e.target.checked})} className="w-4 h-4" /><Label>Auto-renew enabled</Label></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">Create Domain</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search domains..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><Globe className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No domains found</p></CardContent></Card> : (
        <div className="space-y-3">
          {filtered.map((domain) => (
            <Card key={domain.id} className={`hover:shadow-md transition-shadow ${domain.is_expiring_soon ? 'border-l-4 border-l-orange-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Globe className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-slate-800">{domain.domain_name}</h3>
                      {domain.is_expiring_soon && <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" />Expiring Soon</Badge>}
                      {domain.auto_renew && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Auto-renew</Badge>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><p className="text-slate-500">Organization</p><p className="font-medium">{domain.organization_name}</p></div>
                      {domain.registrar && <div><p className="text-slate-500">Registrar</p><p className="font-medium">{domain.registrar}</p></div>}
                      {domain.dns_provider && <div><p className="text-slate-500">DNS</p><p className="font-medium">{domain.dns_provider}</p></div>}
                      {domain.expiry_date && <div><p className="text-slate-500">Expires</p><p className="font-medium">{new Date(domain.expiry_date).toLocaleDateString()}</p></div>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(domain.id, domain.domain_name)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
